import { cleanFloat } from "@/lib/tool-kit/grades/rounding";

/**
 * Course workload in hours per week, after the Course Workload Estimator by Elizabeth
 * (Betsy) Barre and Justin Esarey — Rice University Center for Teaching Excellence
 * (2016, https://cte.rice.edu/workload), extended with Allen Brown at Wake Forest
 * University (2020). The rate tables and the weekly formula below are copied from the
 * estimator's published source (`server.R`, shiny.justinesarey.com/wfuworkloadapp).
 *
 * weekly hours = pages per week ÷ pages per hour
 *              + hours per written page × pages per semester ÷ weeks
 *              + exams × study hours per exam ÷ weeks
 *              + other assignments × hours each ÷ weeks
 *              + class meetings per week × meeting length
 *
 * The estimator's authors call the reading rates research-based and the writing rates
 * "far more speculative" (extrapolated from one study of 493 students).
 */

export type PageDensity = 450 | 600 | 750;
export type Difficulty = "none" | "some" | "many";
export type Purpose = "survey" | "understand" | "engage";
export type WritingDensity = 250 | 500;
export type Genre = "reflection" | "argument" | "research";
export type Drafting = "none" | "minimal" | "extensive";

/**
 * Pages per hour by purpose, difficulty (no / some / many new concepts) and page density
 * (450 words: paperback, 600: monograph, 750: textbook). From 500, 350, 250 (survey),
 * 250, 180, 130 (understand) and 130, 90, 65 (engage) words per minute.
 */
const PAGES_PER_HOUR: Record<
  Purpose,
  Record<Difficulty, Record<PageDensity, number>>
> = {
  survey: {
    none: { 450: 67, 600: 50, 750: 40 },
    some: { 450: 47, 600: 35, 750: 28 },
    many: { 450: 33, 600: 25, 750: 20 },
  },
  understand: {
    none: { 450: 33, 600: 25, 750: 20 },
    some: { 450: 24, 600: 18, 750: 14 },
    many: { 450: 17, 600: 13, 750: 10 },
  },
  engage: {
    none: { 450: 17, 600: 13, 750: 10 },
    some: { 450: 12, 600: 9, 750: 7 },
    many: { 450: 9, 600: 7, 750: 5 },
  },
};

/** Hours per page by genre, drafting and density (250 words double-spaced, 500 single). */
const HOURS_PER_PAGE: Record<
  Genre,
  Record<Drafting, Record<WritingDensity, number>>
> = {
  reflection: {
    none: { 250: 0.75, 500: 1.5 },
    minimal: { 250: 1, 500: 2 },
    extensive: { 250: 1.25, 500: 2.5 },
  },
  argument: {
    none: { 250: 1.5, 500: 3 },
    minimal: { 250: 2, 500: 4 },
    extensive: { 250: 2.5, 500: 5 },
  },
  research: {
    none: { 250: 3, 500: 6 },
    minimal: { 250: 4, 500: 8 },
    extensive: { 250: 5, 500: 10 },
  },
};

export function pagesPerHour(
  purpose: Purpose,
  difficulty: Difficulty,
  density: PageDensity
): number {
  return PAGES_PER_HOUR[purpose][difficulty][density];
}

export function hoursPerPage(
  genre: Genre,
  drafting: Drafting,
  density: WritingDensity
): number {
  return HOURS_PER_PAGE[genre][drafting][density];
}

export interface WorkloadInput {
  weeks: number;
  readingPagesPerWeek: number;
  readingRate: number;
  writingPagesPerSemester: number;
  writingRate: number;
  exams: number;
  studyHoursPerExam: number;
  otherAssignments: number;
  hoursPerAssignment: number;
  meetingsPerWeek: number;
  meetingHours: number;
}

export interface WorkloadResult {
  reading: number;
  writing: number;
  exams: number;
  other: number;
  classTime: number;
  /** Reading, writing, exams and other work: the estimator's "Independent". */
  independent: number;
  total: number;
}

/** Hours per week for each part. Null when the term length or a rate is not positive. */
export function estimateWorkload(input: WorkloadInput): WorkloadResult | null {
  if (input.weeks <= 0 || input.readingRate <= 0 || input.writingRate < 0) {
    return null;
  }
  const reading = input.readingPagesPerWeek / input.readingRate;
  const writing =
    (input.writingRate * input.writingPagesPerSemester) / input.weeks;
  const exams = (input.exams * input.studyHoursPerExam) / input.weeks;
  const other =
    (input.otherAssignments * input.hoursPerAssignment) / input.weeks;
  const classTime = input.meetingsPerWeek * input.meetingHours;
  const independent = reading + writing + exams + other;
  return {
    reading: cleanFloat(reading),
    writing: cleanFloat(writing),
    exams: cleanFloat(exams),
    other: cleanFloat(other),
    classTime: cleanFloat(classTime),
    independent: cleanFloat(independent),
    total: cleanFloat(independent + classTime),
  };
}
